import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { Button } from './ui/Button'
import { Badge, StatusBadge, StatusChip, type StatusVariant } from './ui/Badge'
import { UploadVendorResponsesModal } from './modals/UploadVendorResponsesModal'
import { vendorsApi, evaluationsApi, projectsApi, apiClient } from '../lib/api'
import toast from 'react-hot-toast'
import { 
  FileText, 
  Calendar,
  Users,
  Search,
  Filter,
  Eye,
  MoreVertical,
  FolderOpen,
  Trash2,
  RefreshCw,
  RotateCcw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react'
import {
  trackUnifiedDocumentDownloaded,
  trackVendorResponseReevaluated,
  trackVendorResponseViewed,
  trackVendorResponsesExportedExcel,
  trackVendorResponseStatusChanged,
} from '@/analytics/events'
import { formatDate, formatRelativeTime } from '../lib/utils'
import { generateVendorResponsesExcel } from '../lib/exportUtils'

interface VendorResponse {
  id: string
  vendor_name: string
  vendor_email?: string
  submission_date: string
  file_count: number
  file_names: string[]
  status: 'pending' | 'evaluated' | 'ready_for_review' | 'approved' | 'rejected'
  status_display: string
  page_count?: number
  total_file_size?: number
  unified_document_path?: string
  overall_requirement_status: 'meets' | 'partially_meets' | 'not_met' | 'pending'
  submitted_count?: number
  not_submitted_count?: number
  mandatory_not_submitted_count?: number
}

interface Project {
  id: string
  title: string
  status: string
  due_date?: string
  vendor_responses?: VendorResponse[]
  extraction_status?: 'pending' | 'in_progress' | 'completed' | 'failed'
  extraction_error?: string
  requirements?: any[]
}

interface PaginationProps {
  currentPage: number
  pageSize: number
  totalCount: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

interface ResponsesTabProps {
  project: Project
  pagination?: PaginationProps
  onAddResponse?: () => void
  onUploadVendorResponses?: (vendors: Array<{vendorName: string, files: File[]}>) => void
  onRefresh?: () => void
}

export const ResponsesTab: React.FC<ResponsesTabProps> = ({
  project,
  pagination,
  onAddResponse,
  onUploadVendorResponses,
  onRefresh
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [evaluatingIds, setEvaluatingIds] = useState<Set<string>>(new Set())
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)


  const filteredResponses = project.vendor_responses?.filter(response => {
    const matchesSearch = !searchQuery || 
      response.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      response.vendor_email?.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = !statusFilter || response.status === statusFilter
    
    return matchesSearch && matchesStatus
  }) || []

  // DS Status chip variants (ui/Badge StatusChip):
  // progress = teal, done = chartreuse, warning = orange, error = red, neutral = gray
  const getStatusVariant = (status: string): StatusVariant => {
    switch (status) {
      case 'pending': return 'neutral'
      case 'evaluated': return 'progress'
      case 'ready_for_review': return 'warning'
      case 'approved': return 'done'
      case 'rejected': return 'error'
      default: return 'neutral'
    }
  }

  const getRequirementStatusColor = (status: string) => {
    switch (status) {
      case 'meets': return 'bg-green-100 text-green-600'
      case 'partially_meets': return 'bg-orange-100 text-orange-600'
      case 'not_met': return 'bg-red-100 text-red-700'
      case 'pending': return 'bg-gray-200 text-gray-700'
      default: return 'bg-gray-200 text-gray-700'
    }
  }

  const getRequirementStatusLabel = (status: string) => {
    switch (status) {
      case 'meets': return 'Yes'
      case 'partially_meets': return 'Partially'
      case 'not_met': return 'No'
      case 'pending': return 'Pending'
      default: return 'N/A'
    }
  }

  const getStatusDisplayLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending'
      case 'evaluated': return 'Evaluated'
      case 'ready_for_review': return 'Ready for Review'
      case 'approved': return 'Approved'
      case 'rejected': return 'Rejected'
      default: return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }


  const handleDeleteResponse = async (responseId: string) => {
    if (!window.confirm('Are you sure you want to delete this vendor response? This action cannot be undone.')) {
      return
    }

    setDeletingId(responseId)
    
    try {
      await vendorsApi.delete(responseId)
      toast.success('Vendor response deleted successfully')
      if (onRefresh) {
        onRefresh()
      }
    } catch (error) {
      toast.error('Failed to delete vendor response')
      console.error('Error deleting vendor response:', error)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownloadUnifiedDocument = async (response: VendorResponse) => {
    if (!response.unified_document_path) {
      toast.error('Unified document not available')
      return
    }
    try {
      const encodedPath = encodeURIComponent(response.unified_document_path)
      await apiClient.download(`/files/stream/${encodedPath}`, `${response.vendor_name}_unified.pdf`)
      trackUnifiedDocumentDownloaded(response.id)
      toast.success('Download started')
    } catch (error) {
      toast.error('Failed to download unified document')
      console.error('Error downloading unified document:', error)
    }
  }

  const handleReEvaluateResponse = async (responseId: string, force: boolean = true) => {
    if (!window.confirm('Are you sure you want to re-evaluate this vendor response? This will overwrite existing evaluations.')) {
      return
    }

    setEvaluatingIds(prev => new Set([...prev, responseId]))

    try {
      const result = await evaluationsApi.evaluateResponse(responseId, { force_reevaluation: force })
      trackVendorResponseReevaluated(project.id, { source: 'single', response_count: 1 })
      toast.success('Vendor response re-evaluation started')

      // Immediate refresh - triggers auto-polling to show "Pending" status
      if (onRefresh) {
        onRefresh()
      }

      // Poll for completion (like new vendor evaluation does)
      const taskId = result.task_id
      const pollInterval = setInterval(async () => {
        try {
          const status = await api.get(`/tasks/${taskId}/status`)

          if (status.data.status === 'completed') {
            clearInterval(pollInterval)
            toast.success('Re-evaluation completed!')

            // Invalidate cache and refresh
            queryClient.invalidateQueries(['evaluations', responseId])
            if (onRefresh) {
              onRefresh()
            }
            setEvaluatingIds(prev => {
              const newSet = new Set(prev)
              newSet.delete(responseId)
              return newSet
            })
          } else if (status.data.status === 'failed') {
            clearInterval(pollInterval)
            toast.error('Re-evaluation failed')
            setEvaluatingIds(prev => {
              const newSet = new Set(prev)
              newSet.delete(responseId)
              return newSet
            })
          }
        } catch (error) {
          clearInterval(pollInterval)
          console.error('Error polling task status:', error)
          setEvaluatingIds(prev => {
            const newSet = new Set(prev)
            newSet.delete(responseId)
            return newSet
          })
        }
      }, 2000) // Poll every 2 seconds

    } catch (error) {
      toast.error('Failed to start re-evaluation')
      console.error('Error re-evaluating vendor response:', error)
      setEvaluatingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(responseId)
        return newSet
      })
    }
  }

  const handleReEvaluateAllResponses = async () => {
    const responseIds = filteredResponses.map(r => r.id)
    if (responseIds.length === 0) {
      toast.error('No vendor responses to re-evaluate')
      return
    }

    if (!window.confirm(`Are you sure you want to re-evaluate all ${responseIds.length} vendor responses? This will overwrite existing evaluations.`)) {
      return
    }

    // Set all responses as evaluating
    setEvaluatingIds(new Set(responseIds))

    try {
      const result = await evaluationsApi.batchEvaluate({ vendor_response_ids: responseIds, force_reevaluation: true })

      trackVendorResponseReevaluated(project.id, { source: 'bulk', response_count: responseIds.length })

      // Returns immediately with task_id!
      toast.success(`Started re-evaluation of ${responseIds.length} vendor responses`)

      // Immediate refresh - triggers auto-polling to show "Pending" status
      if (onRefresh) {
        onRefresh()
      }

      // Poll for completion (like new vendor evaluation does)
      const taskId = result.task_id
      const pollInterval = setInterval(async () => {
        try {
          const status = await api.get(`/tasks/${taskId}/status`)

          if (status.data.status === 'completed') {
            clearInterval(pollInterval)
            toast.success('Re-evaluation completed!')

            // Invalidate cache and refresh
            responseIds.forEach(id => {
              queryClient.invalidateQueries(['evaluations', id])
            })
            if (onRefresh) {
              onRefresh()
            }
            setEvaluatingIds(new Set())
          } else if (status.data.status === 'failed') {
            clearInterval(pollInterval)
            toast.error('Re-evaluation failed')
            setEvaluatingIds(new Set())
          }
        } catch (error) {
          clearInterval(pollInterval)
          console.error('Error polling task status:', error)
          setEvaluatingIds(new Set())
        }
      }, 2000) // Poll every 2 seconds

    } catch (error) {
      toast.error('Failed to start batch re-evaluation')
      console.error('Error re-evaluating vendor responses:', error)
      setEvaluatingIds(new Set())
    }
  }

  const handleStatusChange = async (responseId: string, newStatus: string) => {
    setUpdatingStatusId(responseId)
    
    try {
      await vendorsApi.update(responseId, { status: newStatus })
      trackVendorResponseStatusChanged(responseId, { status: newStatus })
      toast.success('Status updated successfully')
      if (onRefresh) {
        onRefresh()
      }
    } catch (error) {
      toast.error('Failed to update status')
      console.error('Error updating status:', error)
    } finally {
      setUpdatingStatusId(null)
    }
  }

  const handleExportToExcel = async () => {
    if (!project || !project.vendor_responses || project.vendor_responses.length === 0) {
      toast.error('No vendor responses to export')
      return
    }

    setIsExporting(true)
    
    try {
      // Try to fetch detailed evaluations for better export data
      let evaluations: any[] = []
      let requirements: any[] = []
      
      try {
        // Fetch requirements for the project
        requirements = await projectsApi.getRequirements(project.id)
      } catch (error) {
        console.warn('Could not fetch project requirements:', error)
      }
      
      try {
        // Fetch evaluations for the project if available
        const evaluationPromises = project.vendor_responses.map(async (response) => {
          try {
            return await evaluationsApi.getResponseEvaluations(response.id)
          } catch (error) {
            // If evaluations aren't available for this response, continue without them
            console.warn(`No evaluations found for response ${response.id}`)
            return []
          }
        })
        
        const evaluationResults = await Promise.all(evaluationPromises)
        evaluations = evaluationResults.flatMap(result => result || [])
      } catch (error) {
        // If we can't fetch evaluations, proceed with basic export
        console.warn('Could not fetch detailed evaluations, proceeding with basic export:', error)
      }

      // Generate and download Excel file
      await generateVendorResponsesExcel(project, evaluations, requirements)
      trackVendorResponsesExportedExcel(project.id, {
        response_count: project.vendor_responses?.length ?? 0,
      })
      toast.success('Excel file downloaded successfully')
      
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export data. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // Check if requirements are extracted
  const requirementsNotReady = !project.extraction_status || 
    project.extraction_status === 'pending' || 
    project.extraction_status === 'in_progress' ||
    project.extraction_status === 'failed'

  const canUploadResponses = project.extraction_status === 'completed' && 
    project.requirements && 
    project.requirements.length > 0

  return (
    <div className="space-y-6">
      {/* Requirements extraction failed — same prominent state as the Requirements tab */}
      {project.extraction_status === 'failed' && (
        <div className="flex flex-col items-center justify-center py-12">
          <img src="/finch-sad.svg" alt="" className="w-28 h-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Requirements Extraction Failed</h3>
          <p className="text-gray-600 text-center max-w-md mb-4">
            {project.extraction_error
              ? `We couldn't extract requirements from the RFP document: ${project.extraction_error}`
              : "We couldn't extract requirements from the RFP document."}{' '}
            Requirements need to be extracted before you can upload and evaluate vendor responses.
          </p>
        </div>
      )}

      {/* Requirements not ready yet (pending / in progress) — same prominent centered state */}
      {requirementsNotReady && project.extraction_status !== 'failed' && (
        <div className="flex flex-col items-center justify-center py-12">
          <img src="/finch-sad.svg" alt="" className="w-28 h-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {project.extraction_status === 'in_progress'
              ? 'Requirements are being extracted'
              : 'Requirements not yet extracted'
            }
          </h3>
          <p className="text-gray-600 text-center max-w-md mb-4">
            {project.extraction_status === 'in_progress'
              ? 'Please wait while we extract requirements from the RFP document. You will be able to upload vendor responses once this process is complete.'
              : 'You need to extract requirements from the RFP document before you can upload and evaluate vendor responses.'
            }
          </p>
          {project.extraction_status === 'in_progress' && (
            <div className="flex items-center text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Extracting requirements...
            </div>
          )}
        </div>
      )}

      {/* Show main content only when requirements are ready */}
      {!requirementsNotReady && (
        <>
          {/* Header — title with primary Upload action aligned to it */}
          <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Vendor Responses ({pagination?.totalCount ?? (project.vendor_responses?.length || 0)})
          </h2>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowBulkUploadModal(true)}
          disabled={!canUploadResponses}
          title={!canUploadResponses ? 'Requirements must be extracted first' : 'Upload vendor responses'}
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          Upload Responses
        </Button>
      </div>

      {/* Filters + table actions toolbar */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-ink-500 transition-colors duration-fast ease-standard"
            />
          </div>
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-3 pr-9 py-2 border border-gray-300 rounded-md text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:border-ink-500 transition-colors duration-fast ease-standard"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="evaluated">Evaluated</option>
            <option value="ready_for_review">Ready for Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Table actions — sit above the table, to the right of the filters */}
        <div className="ml-auto flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={!onRefresh}
            title="Refresh table"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {filteredResponses.length > 0 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReEvaluateAllResponses}
                disabled={evaluatingIds.size > 0}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${evaluatingIds.size > 0 ? 'animate-spin' : ''}`} />
                Re-evaluate All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportToExcel}
                disabled={isExporting}
                title="Export vendor responses to Excel"
              >
                <Download className={`w-4 h-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Responses Table */}
      {filteredResponses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Responses Yet</h3>
          <p className="text-gray-600 mb-4">
            {!canUploadResponses 
              ? 'You need to extract requirements before uploading vendor responses.'
              : 'No vendor responses have been submitted for this project. If you added the project using the NGEM extension, please wait a few more moments and then click on "Refresh".'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Files
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sections Submitted
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meets Requirements
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResponses.map((response, index) => (
                  <tr
                    key={response.id}
                    className="row-enter cursor-pointer transition-colors duration-fast ease-standard hover:bg-gray-50 active:bg-gray-100"
                    style={{ animationDelay: `${index * 40}ms` }}
                    onClick={() => {
                      trackVendorResponseViewed(response.id, { status: response.status })
                      navigate(`/projects/${project.id}/vendors/${response.id}`)
                    }}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {response.vendor_name}
                        </div>
                        {response.vendor_email && (
                          <div className="text-sm text-gray-500">
                            {response.vendor_email}
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-sm text-gray-900 tabular-nums">
                          {response.file_count} file{response.file_count !== 1 ? 's' : ''}
                        </span>
                        {response.files?.some(f => f.status === 'uploading') && (
                          <StatusChip variant="progress">Processing</StatusChip>
                        )}
                        {response.page_count && (
                          <span className="text-xs text-gray-500 tabular-nums">
                            {response.page_count} pages
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 tabular-nums whitespace-nowrap">
                        {formatDate(response.submission_date)}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="relative flex items-center gap-2">
                        {/* Editable status: DS chip (leading icon) wrapping a transparent select */}
                        <StatusChip
                          variant={getStatusVariant(response.status)}
                          className={
                            updatingStatusId === response.id || response.status === 'pending'
                              ? 'opacity-50'
                              : ''
                          }
                        >
                          <select
                            value={response.status}
                            onChange={(e) => handleStatusChange(response.id, e.target.value)}
                            disabled={updatingStatusId === response.id || response.status === 'pending'}
                            className="bg-transparent border-0 p-0 text-[12px] font-normal uppercase leading-none cursor-pointer focus:outline-none disabled:cursor-not-allowed"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {response.status === 'pending' && (
                              <option value="pending">Pending</option>
                            )}
                            <option value="evaluated">Evaluated</option>
                            <option value="ready_for_review">Ready for Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </StatusChip>
                        {response.status === 'pending' && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" title="Analyzing..."></div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      {(response.submitted_count !== undefined && response.submitted_count + (response.not_submitted_count || 0) > 0) ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium tabular-nums ${
                          (response.mandatory_not_submitted_count || 0) > 0
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {response.submitted_count}/{response.submitted_count + (response.not_submitted_count || 0)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${getRequirementStatusColor(response.overall_requirement_status || 'pending')}`}
                        >
                          {getRequirementStatusLabel(response.overall_requirement_status || 'pending')}
                        </Badge>
                        {response.overall_requirement_status === 'pending' && (
                          <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" title="Evaluating requirements..."></div>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {response.unified_document_path && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDownloadUnifiedDocument(response)
                            }}
                            className="p-1 text-green-600 hover:text-green-800"
                            title="Download unified document"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleReEvaluateResponse(response.id)
                          }}
                          disabled={evaluatingIds.has(response.id)}
                          className="p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          title="Re-evaluate"
                        >
                          <RefreshCw className={`w-4 h-4 ${evaluatingIds.has(response.id) ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteResponse(response.id)
                          }}
                          disabled={deletingId === response.id}
                          className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalCount > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                  disabled={pagination.currentPage <= 1}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-fast ease-standard hover:bg-gray-50 active:scale-95 disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-fast ease-standard hover:bg-gray-50 active:scale-95 disabled:pointer-events-none disabled:opacity-40"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(pagination.currentPage - 1) * pagination.pageSize + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalCount}</span> results
                  </p>
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-700">
                      Show:
                    </label>
                    <div className="relative">
                      <select
                        id="pageSize"
                        value={pagination.pageSize}
                        onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                        className="pl-3 pr-9 py-2 rounded-md border border-gray-300 text-sm bg-white appearance-none cursor-pointer focus:outline-none focus:border-ink-500 transition-colors duration-fast ease-standard"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
                <div>
                  <nav className="flex items-center gap-1" aria-label="Pagination">
                    <button
                      onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                      disabled={pagination.currentPage <= 1}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors duration-fast ease-standard hover:bg-gray-100 hover:text-gray-900 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-500 disabled:pointer-events-none disabled:opacity-40"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-[18px] w-[18px]" aria-hidden="true" />
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.currentPage - 2)) + i
                      if (pageNum > pagination.totalPages) return null

                      const isActive = pageNum === pagination.currentPage
                      return (
                        <button
                          key={pageNum}
                          onClick={() => pagination.onPageChange(pageNum)}
                          aria-current={isActive ? 'page' : undefined}
                          className={`inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-3 text-sm font-medium tabular-nums transition-colors duration-fast ease-standard active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-500 ${
                            isActive
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}

                    <button
                      onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                      disabled={pagination.currentPage >= pagination.totalPages}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors duration-fast ease-standard hover:bg-gray-100 hover:text-gray-900 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-500 disabled:pointer-events-none disabled:opacity-40"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-[18px] w-[18px]" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

          {/* Upload Modal */}
          <UploadVendorResponsesModal
            isOpen={showBulkUploadModal}
            onClose={() => setShowBulkUploadModal(false)}
            onSubmit={(vendors) => {
              if (onUploadVendorResponses) {
                return onUploadVendorResponses(vendors)
              }
              return Promise.resolve()
            }}
          />
        </>
      )}
    </div>
  )
}